import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { MessageSquare, FileText, Trash2, Mail, Phone, Calendar, Paperclip } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminEnquiries() {
  const [quotes, setQuotes] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('quotes');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [quotesRes, contactsRes] = await Promise.all([
        axios.get(`${API}/quotes`),
        axios.get(`${API}/contacts`)
      ]);
      setQuotes(quotesRes.data || []);
      setContacts(contactsRes.data || []);
    } catch (error) {
      toast.error('Failed to fetch enquiries');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteQuote = async (id) => {
    if (!window.confirm('Delete this quote request?')) return;
    try {
      await axios.delete(`${API}/admin/quotes/${id}`);
      toast.success('Quote deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  const handleDeleteContact = async (id) => {
    if (!window.confirm('Delete this contact message?')) return;
    try {
      await axios.delete(`${API}/admin/contacts/${id}`);
      toast.success('Contact deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) return <div className="text-white">Loading...</div>;

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-white">Enquiries</h2>
        <p className="text-gray-400 text-sm">View quote requests and contact messages</p>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-gray-700 pb-2">
        <button
          onClick={() => setActiveTab('quotes')}
          className={`px-4 py-2 rounded-t-lg font-medium transition-colors ${
            activeTab === 'quotes'
              ? 'bg-[#F54021] text-white'
              : 'text-gray-400 hover:text-white'
          }`}
        >
          <FileText className="w-4 h-4 inline mr-2" />
          Quote Requests ({quotes.length})
        </button>
        <button
          onClick={() => setActiveTab('contacts')}
          className={`px-4 py-2 rounded-t-lg font-medium transition-colors ${
            activeTab === 'contacts'
              ? 'bg-[#F54021] text-white'
              : 'text-gray-400 hover:text-white'
          }`}
        >
          <MessageSquare className="w-4 h-4 inline mr-2" />
          Contact Messages ({contacts.length})
        </button>
      </div>

      {/* Quote Requests */}
      {activeTab === 'quotes' && (
        <div className="space-y-4">
          {quotes.length === 0 ? (
            <div className="text-center py-12 text-gray-400">No quote requests yet.</div>
          ) : (
            quotes.map((quote) => (
              <div key={quote.id} className="bg-gray-800 rounded-xl border border-gray-700 p-6">
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <h3 className="text-lg font-bold text-white">{quote.name}</h3>
                    <div className="flex flex-wrap gap-4 mt-1 text-sm text-gray-400">
                      <span className="flex items-center gap-1">
                        <Mail className="w-4 h-4" />
                        <a href={`mailto:${quote.email}`} className="hover:text-[#F54021]">{quote.email}</a>
                      </span>
                      {quote.phone && (
                        <span className="flex items-center gap-1">
                          <Phone className="w-4 h-4" />
                          <a href={`tel:${quote.phone}`} className="hover:text-[#F54021]">{quote.phone}</a>
                        </span>
                      )}
                      <span className="flex items-center gap-1">
                        <Calendar className="w-4 h-4" />
                        {formatDate(quote.created_at)}
                      </span>
                    </div>
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleDeleteQuote(quote.id)}
                    className="border-red-600 text-red-400 hover:bg-red-900/30"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
                
                {quote.service_type && (
                  <div className="mb-2">
                    <span className="text-xs bg-[#F54021]/20 text-[#F54021] px-2 py-1 rounded">
                      {quote.service_type}
                    </span>
                  </div>
                )}
                
                <p className="text-gray-300 whitespace-pre-wrap">{quote.project_description}</p>
                
                {quote.attachment_url && (
                  <div className="mt-4 pt-4 border-t border-gray-700">
                    <a 
                      href={`${process.env.REACT_APP_BACKEND_URL}${quote.attachment_url}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 text-[#d4af37] hover:underline"
                    >
                      <Paperclip className="w-4 h-4" />
                      Download: {quote.attachment_name || 'Attachment'}
                    </a>
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      )}

      {/* Contact Messages */}
      {activeTab === 'contacts' && (
        <div className="space-y-4">
          {contacts.length === 0 ? (
            <div className="text-center py-12 text-gray-400">No contact messages yet.</div>
          ) : (
            contacts.map((contact) => (
              <div key={contact.id} className="bg-gray-800 rounded-xl border border-gray-700 p-6">
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <h3 className="text-lg font-bold text-white">{contact.name}</h3>
                    <div className="flex flex-wrap gap-4 mt-1 text-sm text-gray-400">
                      <span className="flex items-center gap-1">
                        <Mail className="w-4 h-4" />
                        <a href={`mailto:${contact.email}`} className="hover:text-[#F54021]">{contact.email}</a>
                      </span>
                      <span className="flex items-center gap-1">
                        <Calendar className="w-4 h-4" />
                        {formatDate(contact.created_at)}
                      </span>
                    </div>
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleDeleteContact(contact.id)}
                    className="border-red-600 text-red-400 hover:bg-red-900/30"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
                
                <p className="text-gray-300 whitespace-pre-wrap">{contact.message}</p>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}
